import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { addDays, getDate, getMonth, startOfMonth, addMonths } from '@e3dc-react/shell/Libs/date'

import { BasicSystemResponseModel, ProductData } from 'src/Libs/shop/models/basic-system.model'
import { DesiredShippingDate } from 'src/Libs/shop'
import { useEffectShopAccess } from 'src/Libs/shop/hooks'
import { TotalMonths, getQntmTotalMonthsFromFixFlex, formatQntmProductListFromFixFlex } from 'src/Libs/shop/utils/basic-system-fix-flex'
import { useShopCartContext } from 'src/Libs/shop/context'
import { useShopChannelType } from 'src/Libs/shop/hooks/use-shop-channel-type'
import { SHOP_CHANNEL_SHIPPING_DAYS } from 'src/Libs/shop/utils/desired-shipping-date-channel-days'
import { ShopChannelType } from 'src/Libs/shop/utils/shop-channel-rules'
import useShopQntmSystemConfiguration from 'src/Libs/shop/hooks/use-qntm-system-configuration'
import {
  resetConfigurator,
  setShippingDate,
  updateConfiguratorId,
  updateLabel,
} from 'src/State/ShopQntmSystemConfiguration/ShopQntmSystemConfigurationAction'

interface UseShopQntmBasicSystemProps {
  desiredShippingDate?: DesiredShippingDate | null
}

interface UseShopQntmBasicSystemReturn {
  onBack: () => void
  isLoading: boolean
  totalMonths: TotalMonths[]
  totalProducts: ProductData[]
  productData: BasicSystemResponseModel | null
  existingSystemConfiguratorShippingDate: Date | null
  handleBasicSystemConfigurationChange: (
    selectedConfigurator: string | null,
    productName: string,
    productId: string,
    productYear: string,
    productMonth: string
  ) => void
}

/*
 * Handles data and selection logic for the Quantum Basic System step
 */
export const useShopQntmBasicSystem = ({ desiredShippingDate }: UseShopQntmBasicSystemProps): UseShopQntmBasicSystemReturn => {
  const { getQntmBasicSystemConfiguration } = useShopQntmSystemConfiguration()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { cartData } = useShopCartContext()
  const shopChannel = useShopChannelType()

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [productData, setProductData] = useState<BasicSystemResponseModel | null>(null)
  const [totalMonths, setTotalMonths] = useState<TotalMonths[]>([])
  const [totalProducts, setTotalProducts] = useState<ProductData[]>([])

  const existingSystemConfiguratorShippingDate =
    desiredShippingDate?.year != null && desiredShippingDate?.month != null && desiredShippingDate?.day != null
      ? new Date(desiredShippingDate.year, desiredShippingDate.month - 1, desiredShippingDate.day)
      : null

  useEffectShopAccess(() => {
    setIsLoading(true)

    if (productData?.elements) {
      const fixFlexTotalMonths = getQntmTotalMonthsFromFixFlex(productData.elements)
      setTotalMonths(fixFlexTotalMonths)

      const formattedProducts = formatQntmProductListFromFixFlex(productData.elements)
      setTotalProducts(formattedProducts)
    }

    setIsLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productData])

  useEffectShopAccess(() => {
    getQntmBasicSystemConfiguration()
      .then(result => setProductData(result))
      .catch(error => console.log(error))
      .finally(() => setIsLoading(false))

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartData])

  const handleBasicSystemConfigurationChange = (
    selectedConfigurator: string | null,
    productName: string,
    productId: string,
    productYear: string,
    productMonth: string
  ): void => {
    const shopSelectedChannel = shopChannel ?? ShopChannelType.ShopE3dc
    const shippingDays = SHOP_CHANNEL_SHIPPING_DAYS[shopSelectedChannel].qntm
    const desiredDate = addDays(new Date(), shippingDays)
    const productDay = getDate(desiredDate)
    const isSameMonth = getMonth(desiredDate) === Number(productMonth)

    const shippingDate = isSameMonth ? productDay : getDate(addDays(startOfMonth(addMonths(desiredDate, 1)), shippingDays))

    if (selectedConfigurator !== productId) {
      dispatch(resetConfigurator())
      dispatch(updateLabel(productName))
    }

    dispatch(setShippingDate(Number(shippingDate), Number(productMonth), Number(productYear)))
    dispatch(updateConfiguratorId(productId))
  }

  const onBack = (): void => {
    navigate('/shop/homepage')
  }

  return {
    onBack,
    isLoading,
    totalMonths,
    totalProducts,
    productData,
    existingSystemConfiguratorShippingDate,
    handleBasicSystemConfigurationChange,
  }
}
